import { Response, Collection } from 'miragejs';
import s3PayloadForRequest from 'garaje/mirage/helpers/s3-payload-for-request';
import config from 'garaje/config/environment';
import { GLOBAL_ADMIN } from 'garaje/utils/roles';
import { compare } from '@ember/utils';
import { faker } from '@faker-js/faker';
import { add } from 'date-fns';
import { paginatedResponse } from 'garaje/mirage/helpers/paginated-response';
import sortCollection from 'garaje/mirage/helpers/sort-collection';
import { getVisitorContactsForTenant } from 'garaje/mirage/utils/get-visitor-contacts-for-tenant';
import { getConnectedTenantsForFilter } from 'garaje/mirage/utils/get-connected-tenants-for-filter';

export default function () {
  // **************************************************************************
  // Multi-tenancy/property management
  // **************************************************************************

  this.urlPrefix = config.apiHost;

  this.post('/a/multi-tenancy/api/direct-uploads', (_schema, request) => {
    return new Response(200, {}, s3PayloadForRequest(this, request));
  });

  this.get('/a/multi-tenancy/api/v1/multi-tenancy-visitor-contacts');
  this.post('/a/multi-tenancy/api/v1/multi-tenancy-visitor-contacts');
  this.delete('/a/multi-tenancy/api/v1/multi-tenancy-visitor-contacts/:id');

  this.get('/a/multi-tenancy/api/v1/zones', function (schema, request) {
    // use first company for scenarios that create an external company that has properties
    const { id: companyId } = schema.companies.first();

    const user = schema.users.find(1);
    if (
      user.companyRoles.filter((role) => role.roleName === GLOBAL_ADMIN).length === 0 &&
      user.zoneRoleIds.length === 0
    ) {
      // user is not a Global Admin and does not have any zones roles, so don't return any zones
      return new Response(403);
    }

    let filteredZones;
    if (request.queryParams['filter[parent]']) {
      filteredZones = schema.zones.where({ parentId: request.queryParams['filter[parent]'], companyId });
    } else {
      filteredZones = schema.zones.where({ parentId: null, companyId });

      // add missing zones that the user has a zone role for
      user.zoneRoles.models.forEach((zoneRole) => {
        if (!filteredZones.models.findBy('id', zoneRole.zone.id)) {
          filteredZones.add(zoneRole.zone);
        }
      });
    }

    if (request.queryParams['filter[name]']) {
      filteredZones = filteredZones.filter(({ name }) =>
        name.toLowerCase().includes(request.queryParams['filter[name]'].toLowerCase())
      );
    }

    filteredZones = sortCollection(filteredZones, request.queryParams.sort);

    return paginatedResponse(this, filteredZones, request);
  });
  this.post('/a/multi-tenancy/api/v1/zones', function ({ zones, companies, configs }) {
    const savedAttrs = this.normalizedRequestAttrs();
    const { coverPhoto } = savedAttrs;

    if (coverPhoto) savedAttrs.coverPhotoUrl = 'https://picsum.photos/400/200';
    const zone = zones.create({ ...savedAttrs, company: companies.first() });

    if (!zone.parentId) {
      configs.create({
        property: zone,
      });
    }

    return zone;
  });
  this.get('/a/multi-tenancy/api/v1/zones/:id');
  this.patch('/a/multi-tenancy/api/v1/zones/:id', function ({ zones }, request) {
    const zone = zones.find(request.params.id);
    const savedAttrs = this.normalizedRequestAttrs();
    const { logo, coverPhoto } = savedAttrs;

    if (logo) savedAttrs.logo = 'https://picsum.photos/400/200';
    if (coverPhoto) {
      savedAttrs.coverPhotoUrl = 'https://picsum.photos/400/200';
    } else if (coverPhoto === null) {
      savedAttrs.coverPhotoUrl = null;
    }

    zone.update(savedAttrs);

    return zone;
  });
  this.delete('/a/multi-tenancy/api/v1/zones/:id', function (schema, request) {
    const { id } = request.params;
    const zone = schema.zones.find(id);
    zone.children.models.forEach((child) => child.destroy());
    zone.destroy();
    return new Response(204, {}, {});
  });

  this.get('/a/multi-tenancy/api/v1/connect-floors', function ({ connectFloors }, request) {
    let floors;

    const propertyFilter = request.queryParams['filter[property]'];
    if (propertyFilter) {
      floors = connectFloors.where({ propertyId: propertyFilter });
    } else {
      floors = connectFloors.all();
    }

    return floors;
  });
  this.post('/a/multi-tenancy/api/v1/connect-floors');
  this.patch('/a/multi-tenancy/api/v1/connect-floors/:id');
  this.delete('/a/multi-tenancy/api/v1/connect-floors/:id');

  this.post('/a/multi-tenancy/api/v1/connect-properties/:id/reorder-floors', function (schema, request) {
    const body = JSON.parse(request.requestBody);

    const ids = body.data.map((item) => item.id);
    // array is ordered from low (position 0) to high (position N)
    const floors = [];
    for (let i = 0; i < ids.length; i++) {
      const floor = schema.connectFloors.find(ids[i]);
      floor.update({ position: i });
      floors.push(floor);
    }

    return new Collection('connect-floor', floors.reverse());
  });

  this.get('/a/multi-tenancy/api/v1/tenants', function ({ tenants }, request) {
    const filter = {};

    if (request.queryParams['filter[property]']) {
      filter.propertyId = request.queryParams['filter[property]'];
    }

    if (request.queryParams['filter[location]']) {
      filter.locationId = request.queryParams['filter[location]'];
    }

    let results = tenants.where(filter);

    if (request.queryParams['filter[name]']) {
      results = results.filter(({ name }) => name.includes(request.queryParams['filter[name]']));
    }

    const getTenantValue = (tenant, key) => {
      if (key === 'tenant-permissions-configuration.employees-read')
        return tenant.tenantPermissionsConfiguration.employeesRead;
      return tenant[key];
    };

    if (request.queryParams.sort) {
      const sortParams = request.queryParams.sort.split(',');
      results = results.sort((a, b) => {
        return sortParams.reduce((acc, sortParam) => {
          const sortDirectionFlag = sortParam.startsWith('-') ? -1 : 1;
          sortParam = sortParam.replace('-', '');
          return acc || sortDirectionFlag * compare(getTenantValue(a, sortParam), getTenantValue(b, sortParam));
        }, 0);
      });
    }

    return paginatedResponse(this, results, request);
  });
  this.get('/a/multi-tenancy/api/v1/tenants/:id');
  this.patch('/a/multi-tenancy/api/v1/tenants/:id');
  this.delete('/a/multi-tenancy/api/v1/tenants/:id');
  this.post('/a/multi-tenancy/api/v1/tenants', function ({ tenants, tenantConnectionRequests, zones }) {
    const savedAttrs = this.normalizedRequestAttrs();

    const property = savedAttrs.propertyId ? zones.find(savedAttrs.propertyId) : null;

    savedAttrs.propertyCompanyName = property?.company.name;
    savedAttrs.propertyName = property?.name;
    savedAttrs.status = 'pending';

    const tenant = tenants.create(savedAttrs);

    tenantConnectionRequests.create({
      tenantId: tenant.id,
      token: faker.string.uuid(),
      expiresAt: add(new Date(), { days: 7 }),
    });

    return tenants.find(tenant.id);
  });

  this.post('/a/multi-tenancy/api/v1/connection-request-invites', function ({ connectionRequestInvites }, request) {
    const savedAttrs = this.normalizedRequestAttrs();

    const requestInvite = connectionRequestInvites.create({
      ...savedAttrs,
      createdAt: new Date(),
      updatedAt: new Date(),
    });

    requestInvite.tenantConnectionRequest.tenant.update({
      status: 'invited',
    });

    return this.serializerOrRegistry.serialize(requestInvite, request);
  });

  this.get('/a/multi-tenancy/api/v1/my-connection-requests', function ({ tenantConnectionRequests, tenants, users }) {
    const me = users.find(1);
    const myTenants = tenants.all().models.map((tenant) => tenant.id);
    const myConnectionRequests = tenantConnectionRequests
      .all()
      .models.filter(
        ({ tenant, connectionRequestInvites }) =>
          myTenants.includes(tenant?.id) &&
          tenant?.status === 'invited' &&
          connectionRequestInvites.models[0].email === me.email
      );

    const collection = new Collection('tenant-connection-request');
    collection.models.push(...myConnectionRequests);

    return collection;
  });

  this.post('/a/multi-tenancy/api/v1/tenants/:id/disconnect', function ({ tenants }, request) {
    tenants.find(request.params.id).update({
      location: null,
      status: 'disconnected',
    });

    return new Response(204);
  });

  this.post('/a/multi-tenancy/api/v1/tenants/:id/regenerate-token', function ({ tenants }, request) {
    const tenant = tenants.find(request.params.id);
    request.queryParams.include = 'tenantConnectionRequest';

    tenant.tenantConnectionRequest.update({
      token: faker.string.uuid(),
    });

    return tenant;
  });

  this.post(
    '/a/multi-tenancy/api/v1/tenant-connection-tokens/:token/claim',
    function ({ tenants, locations }, request) {
      const body = JSON.parse(request.requestBody);
      const location = locations.find(body.location);
      const tenantToLink =
        tenants.where({ locationId: null, status: 'invited' }).models[0] ?? tenants.create('asInvited');

      tenantToLink.update({ location, status: 'connected' });

      if (tenantToLink.property && location.company.id === tenantToLink.property.company.id) {
        location.update({
          property: tenantToLink.property,
        });
      }

      return {
        url: '/tenant-iframe',
      };
    }
  );

  this.get('/a/multi-tenancy/api/v1/visitor-contacts', function (schema, request) {
    const _tenants = getConnectedTenantsForFilter(request, schema);
    let contacts = [];

    _tenants.forEach((tenant) => {
      contacts = [...contacts, ...getVisitorContactsForTenant({ tenant, schema })];
    });

    const response = this.serializerOrRegistry.serialize(schema.visitorContacts.all(), request);
    schema.visitorContacts.all().destroy();

    return new Response(200, {}, response);
  });

  this.patch('/a/multi-tenancy/api/v1/tenant-permissions-configurations/:id');
}
